import { defineAsyncComponent, Directive } from "vue";
import { messageAlert } from "@/composables/message-box";

import { spawnPopover } from "@/components/layout/popover-layer";

const meta = new WeakMap<WeakKey, TooltipData>();

type Modifiers =
    | "noDelay"
    | "noLabel"
    | "left"
    | "right"
    | "top"
    | "bottom"
    | "mfm";
type Args = "dialog";
type Direction = "top" | "right" | "bottom" | "left";
type TooltipData = {
    showTimer?: number;
    hideTimer?: number;
    dispose: (() => void) | null;
    show(direction: Direction): void;
    close(): void;
    click(ev: PointerEvent, dialog: boolean): void;
    showTooltip(delay: number, direction: Direction): void;
    hideTooltip(delay: number): void;
    mountCount: number;
    text?: string;
};

const ToolipComponent = defineAsyncComponent(
    () => import("@/components/tooltips/MagTooltip.vue"),
);

export default {
    created(el: HTMLElement, binding, vnode) {
        const elRef = new WeakRef(el);

        const self = {
            text: binding.value,
            dispose: null,
            close() {
                this.dispose?.();
                this.dispose = null;
            },
            show(direction: Direction) {
                const elDeref = elRef.deref();
                if (
                    !elDeref ||
                    !document.body.contains(elDeref) ||
                    this.dispose ||
                    this.text == null
                ) {
                    return;
                }

                const { resolve, promise } = Promise.withResolvers<void>();
                this.dispose = resolve;

                spawnPopover(
                    ToolipComponent,
                    {
                        text: this.text,
                        asMfm: binding.modifiers.mfm,
                        direction,
                        targetElement: elRef,
                    },
                    undefined,
                    promise,
                ).then();
            },
            click(ev: PointerEvent, dialog: boolean) {
                if (dialog) {
                    ev.preventDefault();
                    ev.stopPropagation();
                    messageAlert({
                        type: "info",
                        text: binding.value,
                    });
                }
            },
            showTooltip(delay: number, direction: Direction) {
                window.clearTimeout(this.showTimer);
                window.clearTimeout(this.hideTimer);
                this.showTimer = window.setTimeout(
                    () => this.show(direction),
                    delay,
                );
            },
            hideTooltip(delay: number) {
                window.clearTimeout(this.showTimer);
                window.clearTimeout(this.hideTimer);
                this.hideTimer = window.setTimeout(() => this.close(), delay);
            },
            mountCount: 0,
            showTimer: undefined as number | undefined,
            hideTimer: undefined as number | undefined,
        } satisfies TooltipData;

        meta.set(el, self);
    },

    beforeMount(el, binding, vnode) {
        const self = meta.get(el);

        if (!self) {
            return;
        }

        self.mountCount += 1;

        if (self.mountCount > 1) {
            console.warn("Duplicate mount of ", el);
        }

        const delay = binding.modifiers.noDelay ? 0 : 100;
        const direction = binding.modifiers.left
            ? "left"
            : binding.modifiers.right
              ? "right"
              : binding.modifiers.top
                ? "top"
                : binding.modifiers.bottom
                  ? "bottom"
                  : "top";

        el.addEventListener(
            "pointerover",
            () => self.showTooltip(delay, direction),
            {
                passive: true,
            },
        );

        el.addEventListener(
            "focusin",
            () => self.showTooltip(delay, direction),
            {
                passive: true,
            },
        );

        el.addEventListener(
            "pointerout",
            (e) => {
                if (!(e.target instanceof Node)) {
                    return;
                }

                if (
                    el.compareDocumentPosition(e.target) &
                    Node.DOCUMENT_POSITION_CONTAINS
                ) {
                    return;
                }

                self.hideTooltip(delay);
            },
            {
                passive: true,
            },
        );

        el.addEventListener("focusout", () => self.hideTooltip(delay), {
            passive: true,
        });

        el.addEventListener("selectstart", (ev) => {
            ev.preventDefault();
        });

        el.addEventListener("click", (ev) =>
            self.click(ev, binding.arg === "dialog"),
        );
    },

    mounted(el, binding, vnode) {
        const self = meta.get(el);

        if (!self) {
            return;
        }

        if (!binding.modifiers.noLabel) {
            if (!document.body.contains(el) || self.text == null) {
                return;
            }

            el.setAttribute("aria-label", self.text);
        }
    },

    updated(el, binding, vnode, prevVnode) {
        const self = meta.get(el);

        if (!self) {
            return;
        }

        self.text = binding.value as string;
    },

    beforeUnmount(el, binding, vnode) {
        const self = meta.get(el);

        if (!self) {
            return;
        }

        self.hideTooltip(0);
    },
} as Directive<HTMLElement, string, Modifiers, Args>;
